import React, { useState } from 'react'
import { toast } from '../toast/CustomToast'

const PayloadViewer = ({ payload, onClose }) => {
    const [showRaw, setShowRaw] = useState(false)

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
        toast.success('Payload copiado!')
    }

    const downloadPayload = () => {
        const blob = new Blob([JSON.stringify(payload.data, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `payload-${payload.trace_id}.json`
        a.click()
        URL.revokeObjectURL(url)
        toast.success('Payload baixado!')
    }

    const formatBytes = (bytes) => {
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    }

    const renderValue = (value, depth = 0) => {
        if (value === null) {
            return <span className="text-gray-500 dark:text-gray-400 italic">null</span>
        }
        if (value === undefined) {
            return <span className="text-gray-500 dark:text-gray-400 italic">undefined</span>
        }
        if (typeof value === 'boolean') {
            return <span className="text-purple-600 dark:text-purple-400 font-semibold">{String(value)}</span>
        }
        if (typeof value === 'number') {
            return <span className="text-blue-600 dark:text-blue-400 font-semibold">{value}</span>
        }
        if (typeof value === 'string') {
            // Detect base64 PDFs/images
            const isBase64 = value.length > 100 && /^[A-Za-z0-9+/=]+$/.test(value)
            if (isBase64) {
                return (
                    <div className="inline-flex items-center gap-2">
                        <span className="text-orange-600 dark:text-orange-400 font-mono text-xs">
                            "[Base64 Data - {formatBytes(value.length)}]"
                        </span>
                        <button
                            onClick={() => copyToClipboard(value)}
                            className="text-xs px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200 dark:hover:bg-orange-900/50 transition"
                        >
                            Copiar
                        </button>
                    </div>
                )
            }
            return <span className="text-green-600 dark:text-green-400">"{value}"</span>
        }
        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="text-gray-500 dark:text-gray-400">[]</span>
            }
            return (
                <div className="pl-4 border-l-2 border-gray-300 dark:border-gray-600">
                    <div className="text-gray-500 dark:text-gray-400 mb-1">[</div>
                    {value.map((item, index) => (
                        <div key={index} className="mb-1">
                            {renderValue(item, depth + 1)}
                            {index < value.length - 1 && <span className="text-gray-500 dark:text-gray-400">,</span>}
                        </div>
                    ))}
                    <div className="text-gray-500 dark:text-gray-400">]</div>
                </div>
            )
        }
        if (typeof value === 'object') {
            const entries = Object.entries(value)
            if (entries.length === 0) {
                return <span className="text-gray-500 dark:text-gray-400">{'{}'}</span>
            }
            return (
                <div className="pl-4 border-l-2 border-blue-300 dark:border-blue-700">
                    <div className="text-gray-500 dark:text-gray-400 mb-1">{'{'}</div>
                    {entries.map(([key, val], index) => (
                        <div key={key} className="mb-1">
                            <span className="text-pink-600 dark:text-pink-400 font-semibold">"{key}"</span>
                            <span className="text-gray-500 dark:text-gray-400">: </span>
                            {renderValue(val, depth + 1)}
                            {index < entries.length - 1 && <span className="text-gray-500 dark:text-gray-400">,</span>}
                        </div>
                    ))}
                    <div className="text-gray-500 dark:text-gray-400">{'}'}</div>
                </div>
            )
        }
        return String(value)
    }

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg">
                            <span className="material-icons text-white" style={{ fontSize: '24px' }}>code</span>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Payload do Evento</h3>
                            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                                <span className="flex items-center gap-1">
                                    <span className="material-icons" style={{ fontSize: '14px' }}>storage</span>
                                    {formatBytes(payload.size_bytes)}
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="material-icons" style={{ fontSize: '14px' }}>fingerprint</span>
                                    {payload.payload_hash.substring(0, 8)}...
                                </span>
                                {payload.compressed && (
                                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                                        Comprimido
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition text-gray-500 dark:text-gray-400"
                    >
                        <span className="material-icons">close</span>
                    </button>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <button
                        onClick={() => setShowRaw(!showRaw)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                            showRaw
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                    >
                        <span className="material-icons" style={{ fontSize: '18px' }}>
                            {showRaw ? 'code' : 'account_tree'}
                        </span>
                        {showRaw ? 'Modo Raw' : 'Modo Árvore'}
                    </button>
                    <button
                        onClick={() => copyToClipboard(JSON.stringify(payload.data, null, 2))}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <span className="material-icons" style={{ fontSize: '18px' }}>content_copy</span>
                        Copiar
                    </button>
                    <button
                        onClick={downloadPayload}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
                        Baixar
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6">
                    {showRaw ? (
                        <pre className="bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto border border-gray-200 dark:border-gray-700">
                            {JSON.stringify(payload.data, null, 2)}
                        </pre>
                    ) : (
                        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg text-sm font-mono overflow-x-auto border border-gray-200 dark:border-gray-700">
                            {renderValue(payload.data)}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition font-medium"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    )
}

export default PayloadViewer
